<template>
  <div v-if="hasActiveFilters" class="bg-gray-800 p-3 rounded-lg mb-6">
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-gray-400 text-sm font-medium">Aktivni filteri:</span>
      <div
        v-for="(value, key) in filters"
        :key="key"
        class="inline-flex items-center bg-gray-700 text-white text-sm rounded-md px-2 py-1"
      >
        <span class="mr-1">
          {{ filterLabels[key] }}:
          {{ getFilterDisplayValue(key, value) }}
        </span>
        <button
          @click="$emit('remove-filter', key)"
          class="ml-1 text-gray-400 hover:text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
      <button
        @click="$emit('reset-filters')"
        class="text-green-500 hover:text-green-400 text-sm font-medium ml-2"
      >
        Očisti sve
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, ref, onMounted } from 'vue';
import categoryService from '@/services/categoryService';

interface Filters {
  search?: string;
  categoryId?: string;
  city?: string;
  minPrice?: number;
  maxPrice?: number;
}

interface Category {
  id: string;
  name: string;
  level?: number;
  parentId?: string;
  children?: Category[];
}

export default defineComponent({
  name: 'ActiveFilters',
  props: {
    filters: {
      type: Object as PropType<Filters>,
      required: true,
    },
    filterLabels: {
      type: Object as PropType<Record<string, string>>,
      required: true,
    },
  },
  emits: ['remove-filter', 'reset-filters'],
  setup(props) {
    const allCategories = ref<Category[]>([]);

    onMounted(async () => {
      // Dohvati sve kategorije u ravnom nizu
      allCategories.value = await categoryService.getAllFlatCategories();
    });

    const hasActiveFilters = computed(() => {
      return Object.keys(props.filters).length > 0;
    });

    const getCategoryPath = (categoryId: string): string => {
      const category = allCategories.value.find((c) => c.id === categoryId);
      if (!category) return categoryId;

      // Ako je kategorija podkategorija, pronađi roditeljsku kategoriju
      if (category.parentId) {
        const parent = allCategories.value.find(
          (c) => c.id === category.parentId
        );
        if (parent) {
          return `${parent.name} > ${category.name}`;
        }
      }

      return category.name;
    };

    const getFilterDisplayValue = (key: string, value: unknown): string => {
      if (key === 'categoryId' && typeof value === 'string') {
        return getCategoryPath(value);
      }
      if (key === 'minPrice' || key === 'maxPrice') {
        return `${value} RSD`;
      }
      return String(value);
    };

    return {
      hasActiveFilters,
      getFilterDisplayValue,
    };
  },
});
</script>
